import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BORDER_RADIUS } from "../../utils/constants";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "./chart";
import {
  Bar,
  BarChart as BarChartLib,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslationApp } from "../../lib/i18next";
import { VALUE_NUMBER } from "../../i18n/keysTranslations";
import { forwardRef } from "react";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { numberDecimal } from "../../utils/numbers";

const BarChart = forwardRef((props, ref) => {
  const {
    chartData = [],
    chartConfig = {},
    xAxisKey = "label",
    title,
    subtitle,
    description,
    hasTooltip = true,
    hasLegend = true,
    hasCartesianGrid = true,
    valueFormat,
    layout = "vertical",
    stacked = true,
    height = 280,
    valueKey = null,
    embedded = false,
    renderTooltip = null,
    renderCategoryAxisTick = null,
    categoryAxisWidth: categoryAxisWidthProp,
    xAxisLabel = null,
    yAxisLabel = null,
    showLegendForValueKey = false,
    barSize = null,
    xAxisDomain = null,
    hideCategoryAxis = false,
    sx = {},
  } = props;

  const { t } = useTranslationApp();
  const isHorizontal = layout === "horizontal";
  const hasCustomCategoryAxisTick =
    isHorizontal && typeof renderCategoryAxisTick === "function";
  const categoryAxisWidth = hideCategoryAxis
    ? 0
    : (categoryAxisWidthProp ?? (hasCustomCategoryAxisTick ? 52 : 120));
  const hasMoreSix = chartData.length > 6;
  const chartConfigKeys = valueKey ? [valueKey] : Object.keys(chartConfig);
  const showLegend = hasLegend && (!valueKey || showLegendForValueKey);
  const axisLabelStyle = {
    textAnchor: "middle",
    fill: "hsl(var(--muted-foreground))",
    fontSize: 12,
  };
  const chartMargin = {
    top: 20,
    left: isHorizontal
      ? hasCustomCategoryAxisTick
        ? 4
        : 8
      : yAxisLabel
        ? 52
        : 12,
    right: 12,
    bottom: isHorizontal ? 8 : xAxisLabel ? 40 : 12,
  };

  const getValueFormatted = (value, name, payload) => {
    const configKey = valueKey || name;
    if (chartConfig?.[configKey]?.valueFormatter) {
      return chartConfig[configKey].valueFormatter(value, payload);
    }
    if (valueFormat) {
      return t(valueFormat.translation, {
        [valueFormat.key]: value,
        ...valueFormat.options,
      });
    }
    return t(VALUE_NUMBER, { value });
  };

  const categoryAxisProps = {
    dataKey: xAxisKey,
    tickLine: false,
    axisLine: false,
    tickMargin: 10,
    interval: isHorizontal ? 0 : "preserveStartEnd",
    width: isHorizontal ? 120 : undefined,
  };

  return (
    <Box
      ref={ref}
      sx={{
        ...(embedded
          ? {
              p: 0,
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
            }
          : {
              padding: 2,
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
            }),
        ...sx,
      }}
    >
      {title && <Typography variant="body1">{title}</Typography>}
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      <ChartContainer
        config={chartConfig}
        className="aspect-auto w-full"
        style={{ height }}
      >
        <BarChartLib
          accessibilityLayer
          data={chartData}
          layout={isHorizontal ? "vertical" : "horizontal"}
          margin={chartMargin}
        >
          {hasCartesianGrid && <CartesianGrid vertical={!isHorizontal} />}
          {hasTooltip && (
            <ChartTooltip
              cursor={
                isHorizontal && renderTooltip
                  ? { fill: "rgba(0,0,0,0.04)" }
                  : false
              }
              wrapperStyle={
                renderTooltip ? { outline: "none", zIndex: 10 } : undefined
              }
              contentStyle={
                renderTooltip
                  ? {
                      padding: 0,
                      margin: 0,
                      background: "transparent",
                      border: "none",
                      boxShadow: "none",
                    }
                  : undefined
              }
              content={
                renderTooltip ? (
                  renderTooltip
                ) : (
                  <ChartTooltipContent
                    labelFormatter={
                      isHorizontal && valueKey
                        ? (_, payload) => payload?.[0]?.payload?.[xAxisKey]
                        : undefined
                    }
                    formatter={(value, name, item) => (
                      <>
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                          style={{
                            "--color-bg":
                              item?.payload?.fill ||
                              `var(--color-${valueKey || name})`,
                          }}
                        />
                        {chartConfig[valueKey || name]?.label || name}
                        <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                          {getValueFormatted(
                            value,
                            valueKey || name,
                            item?.payload
                          )}
                        </div>
                      </>
                    )}
                  />
                )
              }
            />
          )}
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          {isHorizontal ? (
            <>
              <XAxis
                type="number"
                domain={xAxisDomain ?? undefined}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => numberDecimal(value)}
              />
              <YAxis
                type="category"
                dataKey={xAxisKey}
                hide={hideCategoryAxis}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={categoryAxisWidth}
                tick={
                  hasCustomCategoryAxisTick
                    ? (tickProps) => {
                        const item =
                          chartData[tickProps.index] ??
                          chartData.find(
                            (entry) =>
                              entry[xAxisKey] === tickProps.payload?.value
                          );
                        return renderCategoryAxisTick({
                          ...tickProps,
                          item,
                        });
                      }
                    : undefined
                }
              />
            </>
          ) : (
            <>
              <XAxis {...categoryAxisProps}>
                {xAxisLabel ? (
                  <Label
                    value={xAxisLabel}
                    position="insideBottom"
                    offset={-12}
                    style={axisLabelStyle}
                  />
                ) : null}
              </XAxis>
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => numberDecimal(value)}
              >
                {yAxisLabel ? (
                  <Label
                    value={yAxisLabel}
                    angle={-90}
                    position="insideLeft"
                    style={axisLabelStyle}
                  />
                ) : null}
              </YAxis>
            </>
          )}
          {chartConfigKeys.map((key) => (
            <Bar
              key={key}
              dataKey={key}
              fill={`var(--color-${key})`}
              stackId={stacked && !valueKey ? "a" : undefined}
              barSize={barSize ?? undefined}
              radius={isHorizontal ? [0, 8, 8, 0] : 8}
            >
              {valueKey &&
                chartData.map((entry, index) => (
                  <Cell
                    key={`${entry[xAxisKey]}-${index}`}
                    fill={entry.fill || `var(--color-${key})`}
                  />
                ))}
              {chartConfig[key]?.hasLabelList &&
                !hasMoreSix &&
                !isHorizontal && (
                  <LabelList
                    position="top"
                    offset={3}
                    className="fill-foreground"
                    fontSize={12}
                    formatter={(value) => getValueFormatted(value, key)}
                  />
                )}
            </Bar>
          ))}
        </BarChartLib>
      </ChartContainer>
      {description && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {description}
        </Typography>
      )}
    </Box>
  );
});

BarChart.displayName = "BarChart";
export default BarChart;
